import { z } from "zod";
import type { Tables } from "@/integrations/supabase/types";

export type AccountCredit = Tables<"account_credits">;
export const creditStatuses = ["pending", "reserved", "applied", "void"] as const;
export const creditMoney = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
export const canVoidCredit = (credit: Pick<AccountCredit, "status">) => credit.status === "pending";

export function creditSummary(rows: AccountCredit[]) {
  const families = new Map<string, { name: string; email: string; outstanding: number; applied: number; count: number }>();
  let outstanding = 0;
  let applied = 0;
  for (const row of rows) {
    const key = row.parent_email.trim().toLowerCase();
    const family = families.get(key) ?? { name: row.parent_name, email: row.parent_email, outstanding: 0, applied: 0, count: 0 };
    const open = row.status === "pending" || row.status === "reserved" ? row.amount_cents : 0;
    const used = row.status === "applied" ? row.applied_amount_cents ?? 0 : 0;
    outstanding += open;
    applied += used;
    family.outstanding += open;
    family.applied += used;
    family.count++;
    families.set(key, family);
  }
  return { outstanding, applied, families: [...families.values()].sort((a, b) => a.name.localeCompare(b.name)) };
}

export const newCreditSchema = z.object({
  parentName: z.string().trim().min(1, "Enter the parent name.").max(255),
  parentEmail: z.string().trim().email("Enter a valid email address.").max(255),
  childName: z.string().trim().max(255),
  sourceClassId: z.string().uuid("Choose a source class."),
  sourcePaymentId: z.union([z.literal(""), z.string().uuid("Enter a valid source payment UUID.")]),
  amount: z.string().trim().regex(/^\d+(?:\.\d{1,2})?$/, "Enter a dollar amount with at most two decimal places.")
    .transform((value) => Math.round(Number(value) * 100))
    .refine((value) => Number.isSafeInteger(value) && value > 0 && value <= 2147483647, "Enter an amount greater than $0 within the supported range."),
  reason: z.string().trim().min(1, "Enter a reason.").max(2000),
  adminNote: z.string().trim().max(5000),
});